export const DEFAULT_USER_PROFILE = {
  showPicture: true,
  photo: '',
  firstName: '',
  lastName: '',
  gender: '',
  userType: '',
  titleBefore: '',
  titleAfter: '',
  jobTitle: '',
  phone: '',
  email: '',
  birthDate: '',
  nationality: '',
  street: '',
  city: '',
  postalCode: '',
  country: '',
  website: '',
  linkedin: '',
  profile: '',
  jobPreferences: {
    industry: '',
    seniority: 'Entry',
    areaOfResidence: '',
    employmentTypes: [],
    salaryRate: 'Yearly',
    minimumSalary: '',
  },
  experience: [],
  education: [],
  skills: [],
  certifications: [],
  courses: [],
};

const pick = (value) => (typeof value === 'string' ? value.trim() : '');

export const normalizeUserProfile = (profile = {}, user = {}) => {
  const base = {
    ...DEFAULT_USER_PROFILE,
    ...(profile || {}),
    jobPreferences: {
      ...DEFAULT_USER_PROFILE.jobPreferences,
      ...((profile && profile.jobPreferences) || {}),
    },
    experience:     Array.isArray(profile?.experience)     ? profile.experience     : [],
    education:      Array.isArray(profile?.education)      ? profile.education      : [],
    skills:         Array.isArray(profile?.skills)         ? profile.skills         : [],
    certifications: Array.isArray(profile?.certifications) ? profile.certifications : [],
    courses:        Array.isArray(profile?.courses)        ? profile.courses        : [],
  };

  if (!base.email && user.email) base.email = user.email;
  if ((!base.firstName || !base.lastName) && user.name) {
    const parts = user.name.trim().split(/\s+/);
    if (!base.firstName) base.firstName = parts[0] || '';
    if (!base.lastName) base.lastName = parts.slice(1).join(' ');
  }

  return base;
};

export const profileHasDetails = (profile = {}) => {
  const keys = [
    'photo', 'firstName', 'lastName', 'titleBefore', 'titleAfter',
    'jobTitle', 'phone', 'email', 'birthDate', 'nationality',
    'street', 'city', 'postalCode', 'country', 'website', 'linkedin', 'profile',
  ];
  if (keys.some((key) => pick(profile[key]))) return true;
  if (profile.experience?.length > 0) return true;
  if (profile.education?.length > 0) return true;
  if (profile.skills?.length > 0) return true;
  if (profile.certifications?.length > 0) return true;
  if (profile.courses?.length > 0) return true;
  return false;
};

export const PROFILE_STRENGTH_SECTIONS = [
  { id: 'basic',      label: 'Basic Info' },
  { id: 'summary',    label: 'Summary' },
  { id: 'skills',     label: 'Skills' },
  { id: 'experience', label: 'Experience' },
  { id: 'education',  label: 'Education' },
  { id: 'resume',     label: 'Resume' },
];

export const getProfileStrength = (profile = {}, docs = []) => {
  const done = {
    basic: Boolean(pick(profile.firstName) && pick(profile.lastName) && pick(profile.email)),
    summary: Boolean(pick(profile.profile)),
    skills: (profile.skills || []).length > 0,
    experience: (profile.experience || []).length > 0,
    education: (profile.education || []).length > 0,
    resume: (docs || []).some((doc) => doc?.type === 'resume'),
  };
  const sections = PROFILE_STRENGTH_SECTIONS.map((section) => ({ ...section, done: done[section.id] }));
  const completedCount = sections.filter((section) => section.done).length;
  const percent = Math.round((completedCount / sections.length) * 100);
  return { sections, percent, completedCount, total: sections.length };
};

export const getProfileStrengthTier = (percent) => {
  if (percent >= 100) return { label: 'All-Star', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' };
  if (percent >= 75)  return { label: 'Pro',       color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' };
  if (percent >= 50)  return { label: 'Achiever',  color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' };
  if (percent >= 25)  return { label: 'Builder',   color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
  return { label: 'Starter', color: '#EF1D28', bg: '#FFF1F2', border: '#FECDD3' };
};

export const getProfileFullName = (profile = {}, user = {}) => {
  const pieces = [profile.titleBefore, profile.firstName, profile.lastName, profile.titleAfter]
    .map(pick)
    .filter(Boolean);
  return pieces.join(' ') || pick(user.name);
};

const getProfileLocation = (profile = {}) => {
  return [profile.street, profile.city, profile.postalCode, profile.country]
    .map(pick)
    .filter(Boolean)
    .join(', ');
};

export const getProfilePersonalInfo = (profile = {}, user = {}, type = 'resume') => {
  const normalized = normalizeUserProfile(profile, user);
  const photo = normalized.showPicture ? (normalized.photo || user.avatar || '') : '';

  const info = {
    fullName: getProfileFullName(normalized, user),
    jobTitle: pick(normalized.jobTitle),
    email: pick(normalized.email) || pick(user.email),
    phone: pick(normalized.phone),
    location: getProfileLocation(normalized),
    linkedin: pick(normalized.linkedin),
    website: pick(normalized.website),
    summary: pick(normalized.profile),
    summaryTitle: 'Professional Summary',
    dateOfBirth: pick(normalized.birthDate),
    nationality: pick(normalized.nationality),
    sex: '',
    showDOB: false,
    showNationality: false,
    showSex: false,
    photo: photo || null,
  };

  if (type === 'cover_letter') {
    return {
      fullName: info.fullName,
      email: info.email,
      phone: info.phone,
      location: info.location,
    };
  }

  return info;
};

export const applyProfileToDocument = (documentData = {}, profile = {}, user = {}, type = 'resume') => {
  const profileInfo = getProfilePersonalInfo(profile, user, type);
  const normalized = normalizeUserProfile(profile, user);
  const result = {
    ...documentData,
    personalInfo: {
      ...(documentData.personalInfo || {}),
      ...Object.fromEntries(Object.entries(profileInfo).filter(([, value]) => value !== '' && value !== null)),
    },
    profileAutofillApplied: true,
  };

  // Apply arrays only if they have data and the document section is currently empty
  if (type !== 'cover_letter') {
    if (normalized.experience?.length > 0 && !(documentData.experience?.length > 0)) {
      result.experience = normalized.experience;
    }
    if (normalized.education?.length > 0 && !(documentData.education?.length > 0)) {
      result.education = normalized.education;
    }
    if (normalized.skills?.length > 0 && !(documentData.skills?.length > 0)) {
      result.skills = normalized.skills;
    }
    // Courses and certificates live separately in the profile but both fill the
    // document's Certifications section (courses mapped as name/provider/date)
    const certEntries = [
      ...(normalized.certifications || []),
      ...(normalized.courses || []).map(course => ({
        id: course.id,
        name: course.name,
        issuer: course.provider,
        date: course.date,
      })),
    ];
    if (certEntries.length > 0 && !(documentData.certifications?.length > 0)) {
      result.certifications = certEntries;
    }
  }

  return result;
};

export const clearProfileFromDocument = (documentData = {}, type = 'resume') => {
  const cleared = type === 'cover_letter'
    ? { fullName: '', email: '', phone: '', location: '' }
    : {
      fullName: '',
      jobTitle: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      website: '',
      summary: '',
      dateOfBirth: '',
      nationality: '',
      sex: '',
      showDOB: false,
      showNationality: false,
      showSex: false,
      photo: null,
    };
  const personalInfo = {
    ...(documentData.personalInfo || {}),
    ...cleared,
  };

  if (type !== 'cover_letter') {
    personalInfo.summaryTitle = documentData.personalInfo?.summaryTitle || 'Professional Summary';
  }

  return {
    ...documentData,
    personalInfo,
    profileAutofillApplied: false,
  };
};
